import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import BaseForm from "../../formik/BaseForm";
import CustomFormikField from "../../formik/CustomFormikField";
import SubmitButton from "../../formik/SubmitButton";
import UsernameField from "./UsernameField";
import { usePhoneAvailability } from "./hooks/usePhoneAvailability";
import { normalizePhoneNumber } from "../../helper/normalizePhoneNumber";

// ─── Shared styles ───────────────────────────────────────────────────────────

const WHATSAPP_GREEN = "#25D366";
const BRAND_RED = "#FE1F02";
const DARK = "#071F28";
const MUTED = "#5A6B72";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    border: "1px solid #D8DEE1",
    backgroundColor: "#fff",
  },
};

const channelBtnSx = {
  width: "100%",
  minHeight: 52,
  px: 2.4,
  justifyContent: "flex-start",
  borderRadius: "8px",
  backgroundColor: "#fff",
  color: DARK,
  boxShadow: "none",
  fontSize: 16,
  fontWeight: 800,
  textTransform: "none",
  border: "1px solid #D8DEE1",
  transition:
    "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
  "& .MuiButton-startIcon": { mr: 1.4, ml: 0 },
  "&:hover": { backgroundColor: "#F8FAFD" },
};

// ─── Step indicator ──────────────────────────────────────────────────────────

const StepDots = ({ current, total }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: 0.8,
      mb: 2,
    }}
  >
    {Array.from({ length: total }, (_, i) => (
      <Box
        key={i}
        sx={{
          width: i === current ? 24 : 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: i === current ? BRAND_RED : "#D8DEE1",
          transition: "all 300ms ease",
        }}
      />
    ))}
  </Box>
);

// ─── OTP Input (individual digit boxes) ──────────────────────────────────────

const OTP_LENGTH = 6;

const OtpInput = ({ value, onChange, error }) => {
  const inputsRef = useRef([]);

  const focusInput = (index) => {
    if (inputsRef.current[index]) {
      inputsRef.current[index].focus();
      inputsRef.current[index].select();
    }
  };

  const handleChange = (e, index) => {
    const val = e.target.value;
    // Accept only single digit
    if (val && !/^\d$/.test(val)) return;

    const newOtp = value.split("");
    newOtp[index] = val;
    const joined = newOtp.join("");
    onChange(joined);

    // Auto-advance on digit entry
    if (val && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (pasted) {
      onChange(pasted.padEnd(OTP_LENGTH, "").slice(0, OTP_LENGTH));
      focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 1, sm: 1.2 },
        }}
      >
        {Array.from({ length: OTP_LENGTH }, (_, i) => (
          <TextField
            key={i}
            inputRef={(el) => (inputsRef.current[i] = el)}
            value={value[i] || ""}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={i === 0 ? handlePaste : undefined}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              pattern: "[0-9]",
              autoComplete: "one-time-code",
              style: {
                textAlign: "center",
                fontSize: 22,
                fontWeight: 700,
                padding: "10px 0",
                width: 40,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                border: error ? "2px solid #FE1F02" : "1px solid #D8DEE1",
                backgroundColor: "#fff",
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                border: `2px solid ${BRAND_RED}`,
              },
            }}
            size="small"
          />
        ))}
      </Box>
      {error && (
        <Typography
          sx={{
            color: BRAND_RED,
            fontSize: 13,
            textAlign: "center",
            mt: 1,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ─── Phone availability indicator ────────────────────────────────────────────

const PhoneIndicator = ({ status, t }) => {
  const indicators = {
    checking: {
      color: "#6b7280",
      icon: <CircularProgress size={14} sx={{ color: "#6b7280" }} />,
      text: t("phoneAvailability.checking"),
    },
    available: {
      color: "#1E9E52",
      icon: <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />,
      text: t("phoneAvailability.available"),
    },
    taken: {
      color: BRAND_RED,
      icon: <CancelRoundedIcon sx={{ fontSize: 16 }} />,
      text: t("phoneAvailability.taken"),
    },
    invalid: {
      color: "#B4650A",
      icon: <ReportProblemRoundedIcon sx={{ fontSize: 16 }} />,
      text: t("phoneAvailability.invalid"),
    },
    invalid_format: {
      color: "#B4650A",
      icon: <ReportProblemRoundedIcon sx={{ fontSize: 16 }} />,
      text: t("phoneAvailability.invalidFormat"),
    },
  };

  const indicator = indicators[status];
  if (!indicator) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        ml: 0.5,
      }}
    >
      {indicator.icon}
      <Typography
        variant="caption"
        sx={{ color: indicator.color, fontWeight: 600 }}
      >
        {indicator.text}
      </Typography>
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const ProfileCompletionDialog = ({
  open,
  // Step control
  startStep = 0,
  totalSteps = 3,
  // Step 1 — personal info
  personalInitialValues,
  onPersonalInfoSubmit,
  personalLoading,
  showUsernameField,
  showEmailField,
  // Step 2 & 3 — phone + OTP
  userId,
  initialPhone,
  onSavePhone,
  phoneLoading,
  onSendOtp,
  otpSendLoading,
  onVerifyOtp,
  otpVerifyLoading,
  // Shared
  error,
  onClearError,
  onCancel,
}) => {
  const { t } = useTranslation("profileCompletion");
  const [step, setStep] = useState(startStep);
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingChannel, setPendingChannel] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  // Reset when dialog opens / startStep changes
  useEffect(() => {
    if (open) {
      setStep(startStep);
      setOtpCode("");
      setPhoneDigits(initialPhone ? initialPhone.replace(/^\+\d{1,3}/, "") : "");
      if (initialPhone) {
        // Try to extract country code
        const match = initialPhone.match(/^(\+\d{1,3})/);
        if (match) setCountryCode(match[1]);
      }
    }
  }, [open, startStep, initialPhone]);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [resendTimer]);

  // Compose full E.164 phone number
  const fullPhone = normalizePhoneNumber(countryCode, phoneDigits);
  const phoneStatus = usePhoneAvailability(fullPhone, userId);

  const canSendOtp =
    fullPhone.length >= 9 &&
    (phoneStatus === "available" || phoneStatus === "checking") &&
    !phoneLoading &&
    !otpSendLoading;

  // ── Step 1: Personal info form submit ───────────────────────────────────

  const handleStep1Submit = useCallback(
    async (values) => {
      onClearError?.();
      const success = await onPersonalInfoSubmit(values);
      if (success) setStep(1);
    },
    [onPersonalInfoSubmit, onClearError]
  );

  // ── Step 2: Phone channel select ────────────────────────────────────────

  const handleChannelSelect = useCallback(
    async (channel) => {
      onClearError?.();
      setPendingChannel(channel);
      // First save phone, then send OTP
      const phoneSaved = await onSavePhone(fullPhone);
      if (!phoneSaved) return;
      const otpSent = await onSendOtp(fullPhone, channel);
      if (otpSent) {
        setPendingPhone(fullPhone);
        setResendTimer(60);
        setOtpCode("");
        setStep(2);
      }
    },
    [fullPhone, onSavePhone, onSendOtp, onClearError]
  );

  // ── Step 3: Verify OTP ──────────────────────────────────────────────────

  const handleVerify = useCallback(async () => {
    onClearError?.();
    if (otpCode.length < OTP_LENGTH) return;
    await onVerifyOtp(pendingPhone, otpCode);
  }, [otpCode, pendingPhone, onVerifyOtp, onClearError]);

  const handleResend = useCallback(async () => {
    if (resendTimer > 0) return;
    onClearError?.();
    const sent = await onSendOtp(pendingPhone, pendingChannel);
    if (sent) setResendTimer(60);
  }, [pendingPhone, pendingChannel, resendTimer, onSendOtp, onClearError]);

  // ── Validation schema for step 1 ───────────────────────────────────────

  const step1Schema = yup.object({
    firstName: yup.string().trim().required(t("errors.firstName")),
    lastName: yup.string().trim().required(t("errors.lastName")),
    ...(showUsernameField && {
      username: yup
        .string()
        .trim()
        .required(t("errors.usernameRequired"))
        .min(3, t("errors.usernameTooShort"))
        .max(30, t("errors.usernameTooLong"))
        .matches(/^\p{L}{2,}\d*$/u, t("errors.usernameFormat")),
    }),
    ...(showEmailField && {
      email: yup
        .string()
        .trim()
        .required(t("errors.emailRequired"))
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t("errors.emailInvalid")),
    }),
  });

  // ── Compute which "visual" step we're on (for the dots) ─────────────────

  const steps = [];
  if (personalInitialValues) steps.push("personal");
  steps.push("phone");
  steps.push("otp");
  const effectiveTotalSteps = steps.length;
  // Map internal step index (0=personal, 1=phone, 2=otp) to visual dot index
  const dotIndex = step - startStep;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: "#fff",
          p: { xs: 2.5, sm: 3.5 },
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.65)" } },
      }}
    >
      <StepDots current={dotIndex} total={effectiveTotalSteps} />

      {/* ────────────────── Step 1: Personal info ──────────────────────── */}
      {step === 0 && personalInitialValues && (
        <>
          {/* Cancel / close button */}
          {onCancel && (
            <IconButton
              onClick={onCancel}
              sx={{ position: "absolute", top: 14, right: 14, color: MUTED }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            sx={{
              color: DARK,
              fontSize: 22,
              fontWeight: 900,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            {t("step1.title")}
          </Typography>
          <Typography
            sx={{ color: MUTED, fontSize: 14, textAlign: "center", mb: 3 }}
          >
            {t("step1.subtitle")}
          </Typography>

          <BaseForm
            initialValues={personalInitialValues}
            validationSchema={step1Schema}
            onSubmit={handleStep1Submit}
            enableReinitialize
          >
            <Stack spacing={2}>
              <CustomFormikField
                name="firstName"
                placeholder={t("fields.firstName")}
                size="small"
                sx={fieldSx}
              />
              <CustomFormikField
                name="lastName"
                placeholder={t("fields.lastName")}
                size="small"
                sx={fieldSx}
              />
              {showUsernameField && (
                <UsernameField
                  placeholder={t("fields.username")}
                  size="small"
                  sx={fieldSx}
                />
              )}
              {showEmailField && (
                <CustomFormikField
                  name="email"
                  type="email"
                  placeholder={t("fields.email")}
                  size="small"
                  sx={fieldSx}
                />
              )}

              {error && (
                <Typography
                  sx={{
                    color: BRAND_RED,
                    fontSize: 13,
                    textAlign: "center",
                  }}
                >
                  {error}
                </Typography>
              )}

              <Box>
                <SubmitButton disabled={personalLoading}>
                  {personalLoading ? (
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                  ) : (
                    t("buttons.continue")
                  )}
                </SubmitButton>
              </Box>
            </Stack>
          </BaseForm>
        </>
      )}

      {/* ────────────────── Step 2: Phone + channel ────────────────────── */}
      {step === 1 && (
        <>
          {/* Back arrow if step 1 was shown */}
          {personalInitialValues && (
            <IconButton
              onClick={() => {
                onClearError?.();
                setStep(0);
              }}
              sx={{ position: "absolute", top: 14, left: 14, color: DARK }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          )}

          {/* Cancel / close button */}
          {onCancel && (
            <IconButton
              onClick={onCancel}
              sx={{ position: "absolute", top: 14, right: 14, color: MUTED }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            sx={{
              color: DARK,
              fontSize: 22,
              fontWeight: 900,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            {t("step2.title")}
          </Typography>
          <Typography
            sx={{ color: MUTED, fontSize: 14, textAlign: "center", mb: 3 }}
          >
            {t("step2.subtitle")}
          </Typography>

          <Stack spacing={2.5}>
            {/* Phone input with country code */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                size="small"
                sx={{
                  width: 110,
                  height: 40,
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D8DEE1",
                  },
                }}
              >
                <MenuItem value="+1">+1 (US)</MenuItem>
                <MenuItem value="+34">+34 (ES)</MenuItem>
                <MenuItem value="+20">+20 (EG)</MenuItem>
                <MenuItem value="+44">+44 (UK)</MenuItem>
                <MenuItem value="+49">+49 (DE)</MenuItem>
                <MenuItem value="+33">+33 (FR)</MenuItem>
                <MenuItem value="+971">+971 (AE)</MenuItem>
                <MenuItem value="+966">+966 (SA)</MenuItem>
              </Select>
              <TextField
                value={phoneDigits}
                onChange={(e) => {
                  onClearError?.();
                  setPhoneDigits(e.target.value.replace(/[^\d]/g, ""));
                }}
                placeholder={t("fields.phone")}
                type="tel"
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    border: "1px solid #D8DEE1",
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>

            <PhoneIndicator status={phoneStatus} t={t} />

            {error && (
              <Typography
                sx={{ color: BRAND_RED, fontSize: 13, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {/* Channel picker */}
            <Typography
              sx={{
                color: DARK,
                fontSize: 15,
                fontWeight: 700,
                textAlign: "center",
                mt: 1,
              }}
            >
              {t("step2.channelPrompt")}
            </Typography>

            <Stack spacing={1.2}>
              <Button
                type="button"
                variant="outlined"
                disabled={!canSendOtp || phoneStatus !== "available"}
                onClick={() => handleChannelSelect("whatsapp")}
                startIcon={
                  <WhatsAppIcon
                    sx={{ color: WHATSAPP_GREEN, fontSize: 24 }}
                  />
                }
                sx={{
                  ...channelBtnSx,
                  "&:hover": {
                    borderColor: WHATSAPP_GREEN,
                    backgroundColor: "rgba(37,211,102,0.06)",
                    boxShadow: "0 8px 20px rgba(37,211,102,0.15)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                {otpSendLoading && pendingChannel === "whatsapp" ? (
                  <CircularProgress size={20} />
                ) : (
                  t("buttons.whatsapp")
                )}
              </Button>

              <Button
                type="button"
                variant="outlined"
                disabled={!canSendOtp || phoneStatus !== "available"}
                onClick={() => handleChannelSelect("sms")}
                startIcon={
                  <SmsRoundedIcon sx={{ color: BRAND_RED, fontSize: 24 }} />
                }
                sx={{
                  ...channelBtnSx,
                  "&:hover": {
                    borderColor: BRAND_RED,
                    backgroundColor: "rgba(254,31,2,0.05)",
                    boxShadow: "0 8px 20px rgba(254,31,2,0.12)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                {otpSendLoading && pendingChannel === "sms" ? (
                  <CircularProgress size={20} />
                ) : (
                  t("buttons.sms")
                )}
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* ────────────────── Step 3: OTP verification ───────────────────── */}
      {step === 2 && (
        <>
          <IconButton
            onClick={() => {
              onClearError?.();
              setOtpCode("");
              setStep(1);
            }}
            sx={{ position: "absolute", top: 14, left: 14, color: DARK }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          {/* Cancel / close button */}
          {onCancel && (
            <IconButton
              onClick={onCancel}
              sx={{ position: "absolute", top: 14, right: 14, color: MUTED }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            sx={{
              color: DARK,
              fontSize: 22,
              fontWeight: 900,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            {t("step3.title")}
          </Typography>
          <Typography
            sx={{ color: MUTED, fontSize: 14, textAlign: "center", mb: 0.3 }}
          >
            {t("step3.subtitle", { digits: OTP_LENGTH })}
          </Typography>
          <Typography
            sx={{
              color: DARK,
              fontSize: 15,
              fontWeight: 700,
              textAlign: "center",
              mb: 3,
            }}
          >
            {pendingPhone}
          </Typography>

          <Stack spacing={3} sx={{ alignItems: "center" }}>
            <OtpInput
              value={otpCode}
              onChange={(code) => {
                onClearError?.();
                setOtpCode(code);
              }}
              error={error}
            />

            <Button
              variant="contained"
              fullWidth
              disabled={otpCode.length < OTP_LENGTH || otpVerifyLoading}
              onClick={handleVerify}
              sx={{
                backgroundColor: BRAND_RED,
                color: "#fff",
                fontWeight: 800,
                fontSize: 16,
                borderRadius: "8px",
                height: 48,
                textTransform: "none",
                "&:hover": { backgroundColor: "#E01A00" },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  backgroundColor: BRAND_RED,
                  color: "#fff",
                },
              }}
            >
              {otpVerifyLoading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                t("buttons.verify")
              )}
            </Button>

            {/* Resend / change number */}
            <Box sx={{ textAlign: "center" }}>
              {resendTimer > 0 ? (
                <Typography sx={{ color: MUTED, fontSize: 13 }}>
                  {t("step3.resendCountdown", { seconds: resendTimer })}
                </Typography>
              ) : (
                <Typography sx={{ color: MUTED, fontSize: 13 }}>
                  {t("step3.resend")}{" "}
                  <Typography
                    component="span"
                    onClick={handleResend}
                    sx={{
                      color: BRAND_RED,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    {t("step3.resendLink")}
                  </Typography>
                </Typography>
              )}
            </Box>
          </Stack>
        </>
      )}
    </Dialog>
  );
};

export default ProfileCompletionDialog;
